/**
 * Jerarquía de errores tipados. Prohibido `throw new Error(string)` en código de negocio.
 *
 * **Y no lo bloquea ninguna regla ESLint**, aunque esta cabecera lo dijo hasta
 * el 2026-08-28. `no-restricted-syntax` en `.eslintrc.cjs` sólo guarda el
 * `parseFloat` sobre dinero, y ahí está escrito el porqué: hay ~113 sitios
 * heredados y migrarlos es un refactor deliberado, no algo que deba tumbar cada
 * CI. La convención sigue en pie —`docs/conventions.md` §Errores—, pero la
 * sostiene quien escribe, no el linter. Decirlo importa: creerse vigilado por
 * una regla que no existe es peor que saberse a mano.
 */

export type AppErrorContext = Record<string, unknown>;

export class AppError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly context: AppErrorContext;

  constructor(code: string, message: string, httpStatus = 500, context: AppErrorContext = {}) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.context = context;
  }

  toJSON() {
    return { code: this.code, message: this.message, context: this.context };
  }
}

export class ValidationError extends AppError {
  constructor(message: string, context?: AppErrorContext) {
    super('VALIDATION_ERROR', message, 400, context ?? {});
    this.name = 'ValidationError';
  }
}

export class NotFoundError extends AppError {
  constructor(entity: string, id?: string) {
    super('NOT_FOUND', `${entity} not found`, 404, { entity, id });
    this.name = 'NotFoundError';
  }
}

export class ForbiddenError extends AppError {
  constructor(message = 'Forbidden', context?: AppErrorContext) {
    super('FORBIDDEN', message, 403, context ?? {});
    this.name = 'ForbiddenError';
  }
}

export class ConflictError extends AppError {
  constructor(message: string, context?: AppErrorContext) {
    super('CONFLICT', message, 409, context ?? {});
    this.name = 'ConflictError';
  }
}

export class RateLimitError extends AppError {
  constructor(retryAfterSec?: number) {
    super('RATE_LIMIT', 'Too many requests', 429, { retryAfterSec });
    this.name = 'RateLimitError';
  }
}
